import { Observable, Subject, Subscriber } from 'rxjs';

import { catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import { HttpHeaders } from '@angular/common/http';
import { BrowserDetect } from '@util-services/application_utils.service';

import { HttpWrapperService } from '@util-services/http-wrapper/http-wrapper.service';
import { DocumentData, StatusOptions, MeldingType } from '@models';

@Injectable()
export class DocumentLoaderService {
  constructor(private cleHttp: HttpWrapperService) {}

  showDocument<T>(
    url: string,
    data: T,
    documentType: string,
    bestandsnaam?: string,
    openDirect?: boolean,
    bestemming?: string
  ): Observable<DocumentData> {
    const subject = new Subject<DocumentData>();
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers, responseType: 'blob' };

    openDirect = openDirect == null ? true : openDirect;

    this.cleHttp
      .post(url, data, options, false, false, bestemming)
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        catchError((err: any) => {
          subject.error(err);
          const reader: FileReader = new FileReader();
          const obs = Observable.create((observer: Subscriber<unknown>) => {
            reader.onloadend = () => {
              observer.error(JSON.parse(<string>reader.result));

              const responseJSON = JSON.parse(<string>reader.result);
              const content = { content: { errorText: responseJSON.message } as StatusOptions, state: MeldingType.ERROR };

              this.cleHttp.createErrorDialog(content);
              observer.complete();
            };
          });
          reader.readAsText(err);
          subject.complete();
          return obs;
        })
      )
      .subscribe((response: Blob) => {
        let naam: string;
        if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean' || Array.isArray(data)) {
          naam = bestandsnaam;
        } else {
          // Beveiliging op null en undefined wordt gedaan in de openFile, waar de naam gebruikt wordt
          naam = bestandsnaam == null ? data['bestandsnaam'] : bestandsnaam;
        }
        const file = response;

        if (openDirect) {
          this.openFile(file, naam, documentType);
        }

        const fileData = { file: file, naam: naam, documentType: documentType };
        subject.next(fileData);
        subject.complete();
      });
    return subject.asObservable();
  }

  openFileFromB64(file: string | Uint8Array, bestandsnaam: string, documentType: string, sliceSize = 512): void {
    const byteArrays = [];
    if (typeof file === 'string') {
      const byteCharacters = atob(file);

      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }
    } else {
      byteArrays.push(file);
    }
    const blob = new Blob(byteArrays, { type: documentType });
    this.openFile(blob, bestandsnaam, documentType);
  }

  openFile(file: Blob | string, bestandsnaam: string, documentType: string): void {
    const browserDetect = new BrowserDetect();
    browserDetect.init();

    if (bestandsnaam == null) {
      bestandsnaam = 'naamloos';
    }

    if (documentType === 'application/pdf') {
      if (!bestandsnaam.toLowerCase().endsWith('.pdf')) {
        bestandsnaam = bestandsnaam + '.pdf';
      }
    } else if (documentType === 'application/xml') {
      if (!bestandsnaam.toLowerCase().endsWith('.xml')) {
        bestandsnaam = bestandsnaam + '.xml';
      }
    }

    // eslint-disable-next-line node/no-unsupported-features/node-builtins
    const fileURL = URL.createObjectURL(new Blob([file], { type: documentType }));
    if (documentType === 'application/pdf') {
      window.open(fileURL);
    } else {
      // Download het xml-bestand via window.open voor overige browsers
      const a = document.createElement('a');
      a.href = fileURL;
      a.download = bestandsnaam;
      a.type = documentType;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }
}
